<template>
  <section
    class="filter-and-sort"
    :class="{ desktop: $isDesktop }"
    data-nosnippet
  >
    <div ref="scrolling-contents" class="scrolling-contents">
      <header>
        <filter-and-sort-nav-item />
        <span v-if="$isDesktop" class="summary-bar-group">
          <search-summary-bar-details-facets
            :use-link="false"
            class="summary-bar-group"
            :search-filters="updatedSearchFilters"
            @remove-facet="removeFacet"
          />
          <search-summary-bar-details-bounds
            v-if="isBoundsSearch"
            :search-filters="updatedSearchFilters"
            :use-link="false"
            @remove="removeBounds"
          />
          <search-summary-bar-details-price-range
            v-if="isPriceRangeSearch"
            :search-filters="updatedSearchFilters"
            :use-link="false"
            @remove="removePriceRange"
          />
        </span>
      </header>

      <filter-and-sort-sort-options
        v-if="shouldShowSortOptions"
        :options="sortOptions"
        :selected="sortOption"
        :panel-open="searchWizardStore.panelOpen"
        class="filter-and-sort-options"
        @update="updateSort"
      />
      <translate v-if="!$isDesktop" tag="label" class="section-label">
        Filter by
      </translate>

      <span class="filter-accordions">
        <span class="location-section">
          <filter-and-sort-location
            :facet-counts="stableHierarchyCounts"
            :hierarchy-path="hierarchyPath"
            :is-price-search="isPriceSearch"
            :hierarchy-tree="hierarchyTree"
            @hierarchy="updateHierarchy($event)"
          />

          <template v-if="shouldShowWithinInput">
            <div
              class="radius"
              :class="{
                'with-area':
                  searchWizardStore.wizardFilters.area ||
                  shouldShowLocationFilter,
              }"
            >
              <div
                v-if="placename || searchWizardStore.myLocationPoint"
                class="search-area"
              >
                <img
                  src="@/assets/images/location-pin.svg"
                  width="15"
                  height="18"
                />
                <span v-if="placename">{{ placename }}</span>
                <translate v-else-if="searchWizardStore.myLocationPoint">
                  My location
                </translate>
              </div>
              <filter-and-sort-radius
                :radius="radius"
                @radius-change="updateRadius"
              />
            </div>
          </template>
        </span>

        <filter-and-sort-categories
          :selected-category-ids="selectedCategoryIds"
          @change-category-ids="updateSelectedCategories($event)"
          @subcategory-group-change="subcategoryGroupChange($event)"
        />

        <filter-and-sort-price
          v-if="
            isPriceSearch &&
            searchWizardStore.panelOpen &&
            searchMinPrice !== searchMaxPrice
          "
          :disabled="!maxPriceLoaded"
          :dates="updatedSearchFilters?.dates"
          :min-price="searchMinPrice"
          :max-price="searchMaxPrice"
          :price-histogram-data="priceHistogramData"
          :selected-price-range="selectedPriceRange"
          @change="updatePriceRange($event)"
        />
      </span>

      <filter-and-sort-facets
        :facet-counts="stableFacetCounts"
        :initial-facet-counts="stableInitialFacetCounts"
        :selected-facets="selectedFacets"
        @changeFacets="updateSelectedFacets($event)"
      />
    </div>

    <filter-and-sort-footer
      @cancel="onFooterCancel"
      @update-search="
        () => {
          requestIdleCallback(() => goToSearchPage(), { timeout: 1000 })
        }
      "
    />
  </section>
</template>

<script lang="ts">
/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
import { isEqual } from 'lodash-es'
import { HierarchyTree } from '~/apps/hierarchies/domain/types'
import { getHierarchyTree } from '~/apps/hierarchies/inversify.config'
import {
  amountMinorToAmount,
  amountToAmountMinor,
} from '~/utility/amount-minor'
import {
  Component,
  Prop,
  Vue,
  Watch,
  toNative,
} from '~/utility/pu-class-decorator'
import { getMetaFromRoute } from '~/utility/route'
import { yieldToMain } from '~/utility/scheduling'
import { logger } from '../../../../utility/logger'
import { scrollIntoView } from '../../../../utility/scroll'
import { getCategoryFacetLinker } from '../../../categories/services/CategoryFacetLinker'
import { isCampsiteDetailsRoute } from '../../../pu-links/routeHelpers'
import { SearchLinkGenerator } from '../../../url/generators/SearchLinkGenerator'
import { isPointSearch } from '../../domain/isPointSearch'
import type { SearchFilters } from '../../schemas'
import { PriceRange } from '../../types'
import { defineAsyncComponent, hydrateOnIdle } from 'vue'

// Stable empty object for facet counts fallback.
// Inline `|| {}` creates a new object reference every render, which falsely
// busts v-memo in FilterAndSortFacets for ALL groups.
// Stable empty references for prop fallbacks.
// Inline `|| {}` / `|| []` creates new references every render,
// which falsely busts v-memo and triggers unnecessary child re-renders.
const EMPTY_FACET_COUNTS: Record<string, number> = {}
const EMPTY_STRINGS: string[] = []

const AsyncFilterAndSortFacets = defineAsyncComponent({
  loader: () => import('./facets/FilterAndSortFacets.vue'),
  hydrate: hydrateOnIdle(5000),
})

@Component({
  components: {
    FilterAndSortFacets: AsyncFilterAndSortFacets,
  },
  async setup() {
    const searchStore = useSearchStoreSetup(usePinia())
    const searchWizardStore = useSearchWizardStore(usePinia())
    await searchWizardStore.waitForDataLoad()
    const updatedSearchFilters = ref({ ...searchWizardStore.wizardFilters })
    const currentPriceRange = ref(
      searchStore.countSearchFilters?.priceRange || null,
    )
    return {
      searchWizardStore,
      searchStore,
      updatedSearchFilters,
      currentPriceRange,
    }
  },
  emits: ['filtersSubmit'],
})
class FilterAndSortMain extends Vue {
  searchWizardStore: ReturnType<typeof useSearchWizardStore>
  searchStore: ReturnType<typeof useSearchStoreSetup>
  private categoryFacetLinker = getCategoryFacetLinker()

  @Prop()
  placename?: string

  hierarchyTree: HierarchyTree | null = null

  @Prop()
  alreadyOpened: boolean

  // Merged mounted methods - was duplicated causing build warning
  mounted() {
    this.setPriceRangeValues()
    // Initialize CategoryFacetLinker with current language
    this.initializeCategoryFacetLinker()
  }

  @Watch('alreadyOpened')
  async onAlreadyOpenedChanged(val: boolean, oldVal: boolean) {
    if (val && !oldVal) {
      await getHierarchyTree(
        this.hierarchyPath || '/',
        this.$route.params.lang || 'en-gb',
      ).then(({ hierarchyTree }) => {
        this.hierarchyTree = hierarchyTree
      })
    }
  }

  get shouldShowWithinInput() {
    return this.searchWizardStore.shouldShowWithinInput
  }

  updatedSearchFilters: SearchFilters | null
  prevSearchFilters: SearchFilters | null
  private pendingFilterSyncId: ReturnType<typeof requestIdleCallback> | null =
    null

  maxPriceLoaded = true

  private currentPriceRange: SearchFilters['priceRange'] | null

  beforeDestroy() {
    if (this.pendingFilterSyncId !== null) {
      cancelIdleCallback(this.pendingFilterSyncId)
    }
  }

  @Watch('$route.params.lang')
  onLanguageChange() {
    // Reset and reinitialize CategoryFacetLinker when language changes
    this.initializeCategoryFacetLinker()
  }

  private initializeCategoryFacetLinker() {
    const lang = (this.$route.params.lang as string) || 'en-gb'
    this.categoryFacetLinker = getCategoryFacetLinker(lang)
  }

  activated() {
    this.setPriceRangeValues()
  }

  // No { deep: true } — all mutations now use immutable replacement
  // (spread into new object) so the ref value itself changes.
  @Watch('updatedSearchFilters')
  updatedSearchFiltersUpdated() {
    const isSame = isEqual(
      toRaw(this.updatedSearchFilters),
      toRaw(this.prevSearchFilters),
    )
    if (isSame) {
      return
    }
    this.prevSearchFilters = { ...this.updatedSearchFilters }

    if (this.searchWizardStore.panelOpen && this.updatedSearchFilters) {
      // Defer store sync to idle time. The local UI (checkboxes, badges,
      // summary bar) updates immediately from updatedSearchFilters.
      // The expensive work — XState transition, store isEqual deep comparison,
      // counts API call — runs during idle, outside the INP window.
      if (this.pendingFilterSyncId !== null) {
        cancelIdleCallback(this.pendingFilterSyncId)
      }
      this.pendingFilterSyncId = requestIdleCallback(
        () => {
          this.pendingFilterSyncId = null
          if (!this.searchWizardStore.panelOpen) return
          logger(
            'FilterAndSortMain.updatedSearchFiltersUpdated',
            this.searchWizardStore.panelOpen,
            JSON.stringify(this.updatedSearchFilters),
          )
          void Promise.all([
            this.searchWizardStore.setWizardFilters(this.updatedSearchFilters!),
            this.search(),
          ])
        },
        { timeout: 500 },
      )
    }
  }

  @Watch('searchWizardStore.panelOpen')
  async onPanelOpenChanged(val: boolean) {
    if (val && this.updatedSearchFilters) {
      // Yield to main thread so the panel animation paints first.
      // scrollTo must happen after yield — doing it before forces a 5ms
      // synchronous layout on a dirty DOM during the click long task.
      await yieldToMain()
      if (!this.searchWizardStore.panelOpen) return // closed during yield
      ;(this.$refs['scrolling-contents'] as HTMLElement)?.scrollTo(0, 0)
      await this.search()
      this.setPriceRangeValues()
    }
  }

  // No { deep: true } — store replaces wizardFilters.value wholesale
  @Watch('searchWizardStore.wizardFilters')
  wizardFiltersUpdated(newFilters: SearchFilters) {
    // we copy the filters so we can modify them without affecting the wizard
    this.updatedSearchFilters = { ...newFilters }
  }

  // No { deep: true } — store replaces searchFilters.value wholesale
  @Watch('searchStore.searchFilters')
  searchFiltersUpdated(newFilters) {
    // we copy the filters so we can modify them without affecting the wizard
    this.updatedSearchFilters = { ...newFilters }
  }

  setPriceRangeValues() {
    this.currentPriceRange =
      this.searchStore?.countSearchFilters?.priceRange || null
  }

  async search() {
    if (isCampsiteDetailsRoute(this.$route)) {
      return
    }
    // TODO: move hierarchy tree
    await getHierarchyTree(
      this.hierarchyPath || '/',
      this.$route.params.lang || 'en-gb',
    ).then(({ hierarchyTree }) => {
      this.hierarchyTree = hierarchyTree
    })
  }

  updateHierarchy(path: string) {
    if (this.updatedSearchFilters) {
      this.updatedSearchFilters = {
        ...this.updatedSearchFilters,
        hierarchyPath: path === '/' ? undefined : path,
      }
    }
  }

  get hierarchyPath() {
    return this.updatedSearchFilters?.hierarchyPath || '/'
  }

  get shouldShowLocationFilter() {
    return (
      !this.updatedSearchFilters?.area ||
      (this.updatedSearchFilters?.area &&
        this.updatedSearchFilters?.area.isEmptyPoint)
    )
  }

  get shouldShowSortOptions() {
    return this.targetUrl !== 'maps' && !this.$isDesktop
  }

  get sortOptions() {
    if (this.searchStore.context?.results?.sortOptions) {
      return this.searchStore.context.results.sortOptions
    }
    return []
  }

  get selectedCategoryIds() {
    return this.updatedSearchFilters?.categoryIds ?? EMPTY_STRINGS
  }

  get selectedFacets() {
    return this.updatedSearchFilters?.facets ?? EMPTY_STRINGS
  }

  get stableFacetCounts() {
    return this.searchStore?.searchCounts?.keywordsCounts ?? EMPTY_FACET_COUNTS
  }

  get stableInitialFacetCounts() {
    return this.searchStore?.initialCounts?.keywordsCounts ?? EMPTY_FACET_COUNTS
  }

  get stableHierarchyCounts() {
    return this.searchStore?.searchCounts?.hierarchyCounts ?? EMPTY_FACET_COUNTS
  }

  get isBoundsSearch(): boolean {
    return !!this.updatedSearchFilters?.bounds
  }

  updateSelectedCategories(categoryIds: number[]) {
    if (this.updatedSearchFilters) {
      const newCategoryIds = categoryIds.map((id) => id.toString())
      const currentCategoryIds = this.selectedCategoryIds
      const currentFacets = this.selectedFacets

      // Check if any categories were removed and remove their dependent facets
      let updatedFacets = currentFacets
      for (const removedCategoryId of currentCategoryIds) {
        if (!newCategoryIds.includes(removedCategoryId)) {
          // This category was removed, remove its dependent facets
          const result = this.categoryFacetLinker.removeCategoryWithFacets(
            currentCategoryIds,
            updatedFacets,
            removedCategoryId,
          )
          updatedFacets = result.facets
        }
      }

      // Apply as immutable replacement (enables shallow watcher)
      this.updatedSearchFilters = {
        ...this.updatedSearchFilters,
        categoryIds: newCategoryIds,
        facets: updatedFacets,
      }
    }
  }

  subcategoryGroupChange(event: { enabled: string[]; disabled: string[] }) {
    const newFacets = Array.from(
      new Set([
        ...(this.updatedSearchFilters?.facets || []).filter(
          (facet) => !event.disabled.includes(facet),
        ),
        ...event.enabled,
      ]),
    ).sort()

    // When panel is closed, immediately apply facet changes to wizard store
    // like AccommodationWizard does. When panel is open, use temporary filters.
    if (!this.searchWizardStore.panelOpen) {
      void this.searchWizardStore.enableDisableFacets(
        event.enabled,
        event.disabled,
      )
    } else {
      this.updateSelectedFacets(newFacets)
    }
  }

  updateSelectedFacets(slugs: string[]) {
    logger('FilterAndSortMain.updateSelectedFacets', slugs)
    if (this.updatedSearchFilters) {
      // Use CategoryFacetLinker to ensure category-facet relationships are maintained
      let newCategoryIds = this.selectedCategoryIds
      let newFacets = slugs

      // For each facet being added, check if it needs a parent category
      for (const facet of slugs) {
        if (!this.updatedSearchFilters.facets?.includes(facet)) {
          // This is a newly added facet, check if it needs a parent category
          const result = this.categoryFacetLinker.addFacetWithCategory(
            newCategoryIds,
            newFacets.filter((f) => f !== facet), // Remove current facet to avoid duplicates
            facet,
          )
          newCategoryIds = result.categoryIds
          newFacets = result.facets
        }
      }

      // Apply as immutable replacement (enables shallow watcher)
      this.updatedSearchFilters = {
        ...this.updatedSearchFilters,
        categoryIds: newCategoryIds,
        facets: newFacets,
      }
    }
  }

  sortOption = ''

  @Watch('searchStore.context.results.sortOption', { immediate: true })
  setInitialSortOption(sort: string) {
    this.sortOption = sort
  }

  updateSort(sort: string) {
    this.sortOption = sort
  }

  async updateRadius(within: number) {
    this.searchWizardStore.send('SET_WITHIN', { within })

    await this.searchWizardStore.updateCounts().catch((e) => {
      console.warn(
        'Error updating counts:',
        e instanceof Error ? e.message : String(e),
      )
    })
    scrollIntoView('.radius', { block: 'center' })
  }

  get radius() {
    return (
      this.searchWizardStore?.within ||
      this.searchWizardStore?.wizardFilters?.within ||
      this.searchWizardStore?.wizardFilters?.area?.rangeMiles ||
      this.searchStore?.context?.results?.within?.mi ||
      this.$config.public.defaultWithinRange
    )
  }

  get isPriceSearch() {
    return !!this.updatedSearchFilters?.dates
  }

  get priceHistogramData() {
    return this.searchStore?.searchCounts?.priceHistogram
  }

  get searchMinPrice() {
    return amountToAmountMinor(
      this.searchStore?.searchCounts?.priceHistogram?.bins[0] || 0,
    )
  }

  get searchMaxPrice() {
    const searchResultMaxPrice = this.searchStore?.searchCounts?.resultMaxPrice
    const searchMaxPrice = this.searchStore?.searchCounts?.maxPrice
    return amountToAmountMinor(searchMaxPrice || searchResultMaxPrice)
  }

  get selectedPriceRange() {
    const range = this.currentPriceRange
    return range
      ? [
          range.min ? amountToAmountMinor(range.min) : 0,
          range.max
            ? isFinite(range.max)
              ? amountToAmountMinor(range.max)
              : this.searchMaxPrice
            : this.searchMaxPrice,
        ]
      : undefined
  }

  updatePriceRange({ manual, range }: { range: PriceRange; manual: boolean }) {
    const newRange = {
      min: range.min === 0 ? 0 : amountMinorToAmount(range.min),
      max: range.max === Infinity ? 0 : amountMinorToAmount(range.max),
    }
    if (manual) {
      this.currentPriceRange = newRange
    }
    this.updateSearchFiltersPrice(newRange)
  }

  updateSearchFiltersPrice(price) {
    if (this.updatedSearchFilters) {
      this.updatedSearchFilters = {
        ...this.updatedSearchFilters,
        priceRange: price,
      }
    }
  }

  async goToSearchPage() {
    const { path, query } = this.getSearchPageUrlData()
    await this.$router.push(
      this.$puLocalePath({
        path,
        query: query as { [key: string]: string },
      }),
    )
    this.$emit('filtersSubmit')
  }

  private getSearchPageUrlData() {
    const filters = this.getSearchPageFilters()
    const input = {
      category: this.targetUrl,
      customQueryParams: this.placename
        ? { placename: this.placename }
        : undefined,
      filters: { ...filters, page: undefined },
      hierarchyPath: this.updatedSearchFilters?.hierarchyPath,
    }
    return new SearchLinkGenerator().getUrlData(input)
  }

  get targetUrl() {
    const meta = getMetaFromRoute(this.$route)
    return (meta.type && meta.type === 'maps') ||
      this.searchStore?.searchFilters?.base === 'maps'
      ? 'maps'
      : 'search'
  }

  private getSearchPageFilters(): SearchFilters {
    const bounds = this.updatedSearchFilters?.bounds
    return {
      ...this.updatedSearchFilters,
      area: this.areaParamForSearchPageFilters,
      hierarchyPath: undefined,
      bounds: this.updatedSearchFilters?.hierarchyPath ? undefined : bounds,
      sort: this.sortingParamForSearchPageFilters,
    }
  }

  private get areaParamForSearchPageFilters() {
    return this.updatedSearchFilters?.area &&
      isPointSearch(this.updatedSearchFilters)
      ? this.updatedSearchFilters.area
      : undefined
  }

  private get sortingParamForSearchPageFilters() {
    return this.updatedSearchFilters &&
      !isPointSearch(this.updatedSearchFilters) &&
      this.sortOption === 'distance'
      ? ''
      : this.sortOption
  }

  get isPriceRangeSearch(): boolean {
    const priceRange = this.updatedSearchFilters?.priceRange || undefined
    return !!(priceRange?.min || priceRange?.max)
  }

  async onFooterCancel() {
    await this.searchWizardStore.resetToPreviousFilters()
    this.$emit('cancel')
  }

  removeFacet(facet: string): void {
    logger('FilterAndSortMain.removeFacet', facet)
    const facets = this.updatedSearchFilters?.facets.filter((f) => f !== facet)
    this.updateSelectedFacets(facets)
  }

  removeBounds(previousSearchFilters: SearchFilters) {
    if (this.updatedSearchFilters) {
      this.updatedSearchFilters = previousSearchFilters
    }
  }

  removePriceRange() {
    this.updatePriceRange({
      range: {},
      manual: true,
    })
  }
}

export default toNative(FilterAndSortMain)
</script>

<style scoped>
.filter-and-sort {
  background-color: var(--colour-page-background);
  bottom: 0;
  left: 0;
  margin: 0 auto;
  position: fixed;
  right: 0;
  top: 0;
  display: flex;
  flex-direction: column;
  height: 100dvh;
  border: 1px solid transparent;
}

.scrolling-contents {
  height: 100%;
  overflow-x: hidden;
  overflow-y: scroll;
  padding-bottom: 8em;
}

.filter-and-sort-options {
  min-height: 26vw;
}

.filter-and-sort :deep(.ui-collapse) {
  margin: 0 0 1.5em 0;
  background: white;
  border-bottom-color: var(--colour-page-background);
  border-top-color: var(--colour-page-background);
  -webkit-transform: translate3d(0, 0, 0);
}

.filter-and-sort :deep(.ui-collapse-title) {
  font-weight: normal;
  font-size: var(--body);
}

.filter-and-sort :deep(.ui-collapse-header) {
  padding: 0.5em 1em;
  min-height: 3em;
}

header {
  min-height: 2.75em;
  background: white;
  display: flex;
}

.radius {
  background: white;
  padding: 0.5em 1em 0.65em;
}

.radius :deep(label) {
  font-weight: normal;
}

.section-label {
  color: var(--colour-dark-blue);
  display: block;
  padding: 1em 0 0.5em 1em;
}

.search-area {
  font-size: var(--body);
  margin: 0 0 0.5em 0;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.search-area img {
  height: 1.25em;
  width: auto;
  margin-right: 0.25em;
}

.radius.with-area {
  margin-bottom: 1.5em;
  min-height: 3.1em;
}

.ui-collapse.hierarchy + .radius.with-area {
  margin-top: -1.5em;
}

.filter-accordions :deep(.electric-facet-toggle) {
  display: none;
}

/* desktop & tablet */
.filter-and-sort.desktop {
  background: none;
}

.filter-and-sort.desktop :deep(.filter-and-sort-nav-item) {
  background: none;
  border-radius: 0;
  padding: 0;
  margin-right: 2em;
}

.filter-and-sort.desktop :deep(.text) {
  color: var(--colour-wcag-blue);
  font-size: 1.3em;
}

.filter-and-sort.desktop .scrolling-contents :deep(.filter-and-sort-nav-item) {
  align-self: center;
}

.filter-and-sort.desktop
  .scrolling-contents
  :deep(.filter-and-sort-nav-item img) {
  display: none;
}

.filter-and-sort.desktop .summary-bar-group .summary-bar-group {
  margin-right: 0;
}

.desktop .filter-and-sort-options {
  min-height: 0;
}

.filter-and-sort.desktop .filter-accordions :deep(.ui-collapse-header) {
  padding: 0.5em 1em 0.5em 0;
}

.filter-and-sort.desktop :deep(.ui-collapse) {
  margin: 0;
}

.filter-and-sort.desktop :deep(.section-title),
.filter-and-sort.desktop :deep(.section-label) {
  font-size: 1.5em;
  font-weight: normal;
}

.filter-and-sort.desktop :deep(.section-title) {
  padding-left: 0;
}

.filter-and-sort.desktop :deep(.section-label) {
  padding: 1.5em 0 0 1.4em;
  margin-bottom: 0.25em;
}

.filter-and-sort.desktop :deep(.sort-wrapper .radio-group) {
  padding-left: 0;
}

.filter-and-sort.desktop .ui-collapse.hierarchy + .radius.with-area {
  margin-top: 0;
  padding-left: 0;
}
</style>
